import FeaturedArticle from 'components/ArticleGrid/LargeFeaturedArticle';
import { cn, getColorway, getYMargins } from 'utils/styles';
import SmallArticle from 'components/ArticleGrid/SmallArticle';
import {
  ArticleGridProps,
  RelatedArticleFlexibleProps,
  RelatedArticleProps,
  ResourceProps,
} from 'libs/contentful/props';
import { ArticleGridCardStyleProps } from './types';
import ArticleRowCarousel from 'components/ArticleGrid/ArticleRowCarousel';
import ArticleGridCarousel from 'components/ArticleGrid/ArticleGridCarousel';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import Button from 'components/shared/Buttons/BaseButton';
import MediumArticle from 'components/ArticleGrid/MediumArticle';
import { Colorways } from 'types/constants';
import { liveUpdateAttrs } from 'libs/contentful/utils';
import { getRichTextValue } from '@/utils/formatters';
import {
  fetchEntriesByTaxonomyREST,
  adaptEntriesForArticleGrid,
  ArticleGridItem,
  extractEntryConceptIds,
  fetchConceptDetails,
  type ResolvedConcept,
} from 'libs/contentful/rest';
import { isDraftPreviewEnabled } from 'utils/draftPreview';
import { getPageContext } from 'utils/pageContext';
import ArticleGridPaginated from './ArticleGridPaginated';

interface TaxonomyFetchResult {
  articles: ArticleGridItem[];
  /** Map of entry sys.id to its tagged concept IDs */
  entryConceptIds: Map<string, string[]>;
  /** Map of concept ID to resolved concept details (label + hierarchy) */
  concepts: Map<string, ResolvedConcept>;
}

/**
 * Fetches articles by taxonomy ID and resolves concept labels for category display.
 * Supports PageBlogPostFlexible, BlogPostPage (blogPage), and Resource content types.
 */
async function fetchArticlesByTaxonomy(taxonomyId: string, subdomain?: string): Promise<TaxonomyFetchResult> {
  const draftPreview = await isDraftPreviewEnabled();
  const { entries, includes } = await fetchEntriesByTaxonomyREST(taxonomyId, draftPreview, { subdomain });

  if (entries.length === 0) {
    return { articles: [], entryConceptIds: new Map(), concepts: new Map() };
  }

  const articles = adaptEntriesForArticleGrid(entries, includes);

  // Build a map of entry ID -> concept IDs from the raw entries
  const entryConceptIds = extractEntryConceptIds(entries);

  // Fetch concept details (labels + broader info) for all tagged concepts
  const allConceptIds = [...entryConceptIds.values()].flat();
  const concepts = await fetchConceptDetails(allConceptIds, draftPreview);

  return { articles, entryConceptIds, concepts };
}

/**
 * Finds the most specific (deepest / most nested) concept label for an article.
 *
 * Among the article's tagged concepts, the "most specific" is the one that is NOT
 * a parent of any other concept in the set — i.e. the leaf in the hierarchy.
 * Falls back to the selected taxonomy label when concepts can't be resolved.
 */
function getMostSpecificConceptLabel(
  articleId: string,
  fallbackLabel: string,
  entryConceptIds: Map<string, string[]>,
  concepts: Map<string, ResolvedConcept>,
): string {
  const conceptIds = entryConceptIds.get(articleId);
  if (!conceptIds || conceptIds.length === 0) return fallbackLabel;

  // Single concept — use it directly
  if (conceptIds.length === 1) {
    return concepts.get(conceptIds[0])?.label ?? fallbackLabel;
  }

  // Multiple concepts — find the leaf (not an ancestor of any other concept).
  // Collect all parent IDs referenced by broader across the article's concepts.
  const articleConceptSet = new Set(conceptIds);
  const parentIds = new Set<string>();
  for (const id of conceptIds) {
    const concept = concepts.get(id);
    if (concept) {
      for (const broaderId of concept.broaderIds) {
        if (articleConceptSet.has(broaderId)) {
          parentIds.add(broaderId);
        }
      }
    }
  }

  // The leaf concept is one that is NOT in the parentIds set.
  // When multiple leaves exist (siblings at the same depth), prefer the last one
  // in the array — the most recently tagged concept is typically the intended primary category.
  const leaves = conceptIds.filter(id => !parentIds.has(id));
  const leafId = leaves[leaves.length - 1] ?? conceptIds[conceptIds.length - 1];
  return concepts.get(leafId)?.label ?? fallbackLabel;
}

export enum ArticleGridVariant {
  Article = 'Article',
  Featured = 'Featured',
  Explore = 'Explore',
  ShortCta = 'Short CTA',
  Grid = 'Grid',
  Paginated = 'Paginated',
}

const extractSlugFromUrl = (url: string | undefined): string => {
  if (!url) return '';

  try {
    // If it's a full URL, extract pathname + search params
    const urlObj = new URL(url);
    const pathAndSearch = urlObj.pathname + urlObj.search;
    // Remove leading slash
    return pathAndSearch.startsWith('/') ? pathAndSearch.slice(1) : pathAndSearch;
  } catch {
    // If it's not a valid URL, assume it's already a correctly formatted slug/path
    return url;
  }
};

const resourceToArticleShim = (
  item: ResourceProps | RelatedArticleProps | RelatedArticleFlexibleProps,
  taxonomyLabel?: string,
): RelatedArticleProps => {
  const getCategory = (defaultCategory?: { label: string }) => {
    if (taxonomyLabel) {
      return { label: taxonomyLabel };
    }
    return defaultCategory;
  };

  if (item.__typename === 'Resource') {
    const defaultCategory = item?.resourceLabelRte ? { label: getRichTextValue(item.resourceLabelRte) } : undefined;
    return {
      __typename: 'RelatedArticle',
      articleTitle: item.resourceCopy,
      articleImage: item.resourceIcon,
      slug: item.resourcePage?.slug || extractSlugFromUrl(item.url),
      category: getCategory(defaultCategory),
      sys: item?.sys || { id: '' },
    };
  }

  if (item.__typename === 'PageBlogPostFlexible') {
    return {
      __typename: 'RelatedArticle',
      articleTitle: item.articleTitle?.headline,
      articleImage: item.articleImage?.desktopMedia,
      slug: item.slug,
      category: getCategory(),
      categories: item.categories,
      sys: item.sys,
      publishDate: item.publishDate,
    };
  }

  const relatedArticle = item as RelatedArticleProps;
  return {
    ...relatedArticle,
    category: getCategory(relatedArticle.category),
  };
};

/**
 * Props for the internal ArticleGridContent component.
 * Includes pre-fetched taxonomy articles if applicable.
 */
interface ArticleGridContentProps extends ArticleGridProps {
  /** Pre-fetched articles from taxonomy (already shimmed) */
  taxonomyArticles?: RelatedArticleProps[];
}

/**
 * Internal synchronous content component for ArticleGrid.
 */
const ArticleGridContent = ({
  variant,
  sectionName,
  color,
  margins,
  featuredArticleColor,
  articlesCollection,
  headline,
  cta,
  pullFromTaxonomy,
  taxonomyArticles = [],
  useSolidCardBackgrounds,
  maxArticles,
  articlesPerPage,
  sys,
  showDates,
}: ArticleGridContentProps) => {
  // When taxonomy mode is active, use only taxonomy articles (no fallback to articlesCollection)
  // This ensures the component renders nothing when taxonomy is selected but returns no content
  const articles =
    pullFromTaxonomy?.value && pullFromTaxonomy?.label
      ? taxonomyArticles
      : articlesCollection.items.map(article => resourceToArticleShim(article));

  const useArticleGridCarouselLayout = variant === ArticleGridVariant.Explore;
  const useArticleLayout = variant === ArticleGridVariant.Article;
  const useFeaturedLayout = variant === ArticleGridVariant.Featured;
  const useShortCtaLayout = variant === ArticleGridVariant.ShortCta;
  const useGridLayout = variant === ArticleGridVariant.Grid;
  const usePaginatedLayout = variant === ArticleGridVariant.Paginated;

  const articlesSlice = articles.slice(0, maxArticles ?? 3);

  if (!articles.length) return null;

  let cardStyles: ArticleGridCardStyleProps;
  if (useSolidCardBackgrounds) {
    cardStyles = {
      colorway:
        color.name === Colorways.Primary ? 'light-green-colorway rounded-lg gap-0' : 'white-colorway rounded-lg gap-0',
      text: 'p-6',
      image: 'rounded-t-lg rounded-b-none',
    };
  }

  return (
    <section
      {...(sectionName != null && { id: encodeURIComponent(sectionName) })}
      className={cn(getColorway(color), getYMargins(margins))}>
      <div className="content-wrapper flex flex-col">
        {useGridLayout && (
          <>
            {headline && (
              <div
                className="font-sans font-semibold text-heading-md md:text-heading-lg mb-12"
                {...liveUpdateAttrs(sys.id, 'headline')}>
                <RichTextHeadline headline={headline} />
              </div>
            )}
            <div className="grid sm:grid-cols-2 md:grid-cols-3 grid-cols-1 gap-x-2 md:gap-x-8 gap-y-8">
              {articles.map((article, i) => (
                <SmallArticle {...article} articleImage={undefined} key={i} showFirstBorder />
              ))}
            </div>
          </>
        )}
        {useArticleLayout && (
          <ArticleRowCarousel color={color} totalItems={articlesSlice.length}>
            {articlesSlice.map((article, i) => (
              <MediumArticle {...article} cardStyles={cardStyles} showDates={showDates} key={i} />
            ))}
          </ArticleRowCarousel>
        )}

        {useFeaturedLayout && (
          <div className={cn('grid md:grid-cols-3 grid-cols-1 grid-rows-1 gap-8')}>
            <div className="col-span-2">
              <FeaturedArticle {...articles[0]} featuredArticleColor={featuredArticleColor} />
            </div>

            <div className="grid grid-rows-3 gap-12 items-center">
              {articles.slice(1, 4).map((article, i) => {
                return <SmallArticle {...article} key={i} />;
              })}
            </div>
          </div>
        )}

        {useArticleGridCarouselLayout && <ArticleGridCarousel color={color} articles={articles} />}

        {useShortCtaLayout && (
          <div className={'flex flex-col md:flex-row md:justify-between gap-8'}>
            <div className={'flex flex-col w-[314px] gap-5'}>
              {headline && (
                <div
                  className="font-sans font-semibold text-heading-md md:text-heading-lg"
                  {...liveUpdateAttrs(sys.id, 'headline')}>
                  <RichTextHeadline headline={headline} />
                </div>
              )}
              {cta && (
                <div className="hidden md:block max-w-max">
                  <Button href={cta?.url} sys={cta?.sys} internalPage={cta?.internalPage} variant={'outline'}>
                    {cta?.text}
                  </Button>
                </div>
              )}
            </div>
            <div className={'max-w-[990px]'}>
              <ArticleRowCarousel color={color} totalItems={articlesSlice.length}>
                {articlesSlice.map((article, i) => (
                  <SmallArticle {...article} key={i} />
                ))}
              </ArticleRowCarousel>
            </div>

            {cta && (
              <div className="block md:hidden max-w-max">
                <Button href={cta?.url} sys={cta?.sys} internalPage={cta?.internalPage} variant={'outline'}>
                  {cta?.text}
                </Button>
              </div>
            )}
          </div>
        )}

        {usePaginatedLayout && (
          <>
            {headline && (
              <div
                className="font-sans font-semibold text-heading-md md:text-heading-lg mb-12"
                {...liveUpdateAttrs(sys.id, 'headline')}>
                <RichTextHeadline headline={headline} />
              </div>
            )}
            <ArticleGridPaginated articlesPerPage={articlesPerPage}>
              {articles.map((article, i) => (
                <MediumArticle {...article} cardStyles={cardStyles} showDates={showDates} key={i} />
              ))}
            </ArticleGridPaginated>
          </>
        )}
      </div>
    </section>
  );
};

/**
 * Async Server Component that fetches taxonomy articles and renders the grid.
 * Supports fetching PageBlogPostFlexible, BlogPostPage, and Resource entries by taxonomy.
 */
const ArticleGrid = async (props: ArticleGridProps) => {
  let taxonomyArticles: RelatedArticleProps[] = [];

  // Fetch articles by taxonomy and resolve per-article concept labels
  if (props.pullFromTaxonomy?.value && props.pullFromTaxonomy?.label) {
    const { subdomain } = await getPageContext();
    const { articles, entryConceptIds, concepts } = await fetchArticlesByTaxonomy(
      props.pullFromTaxonomy.value,
      subdomain,
    );
    const fallbackLabel = props.pullFromTaxonomy.label;

    taxonomyArticles = articles.map(article => {
      const articleId = article.sys?.id ?? '';
      const label = getMostSpecificConceptLabel(articleId, fallbackLabel, entryConceptIds, concepts);
      return resourceToArticleShim(article, label);
    });
  }

  return <ArticleGridContent {...props} taxonomyArticles={taxonomyArticles} />;
};

export default ArticleGrid;
